import { EyeIcon, PlayIconFilledIcon } from 'assets/icons';
import { ReactComponent as Logo } from 'assets/images/logo-gray.svg';
import globalStyles from 'assets/stylesheets/global-styles.module.scss';
import { Button, ButtonSize, ButtonStyle, ButtonType } from 'common/button';
import { Breakpoints } from 'common/enums';
import { Footer, FooterType } from 'common/footer';
import { Input } from 'common/input';
import { LegacyVideoModal } from 'common/legacy-video-modal/legacy-video-modal';
import { LegacyVideoSection } from 'common/legacy-video-section/legacy-video-section';
import { Notification } from 'common/notification';
import { Spinner } from 'common/spinner';
import { errorsCode, isBeta } from 'config/constants';
import { AppContext } from 'context';
import CypressIds from 'cypressIds';
import { trackCustomEvent } from 'helpers/analytics';
import { LocalStorageApi } from 'helpers/local-storage';
import {
  classnames,
  hasNotUserValidatedEmail,
  isEmailValid,
  redictDependingFailingStatus,
} from 'helpers/utils';
import { useMediaQuery } from 'hooks/use-media-query';
import { UserSignIn } from 'models/user-sign-in';
import { UserController } from 'networking/controllers/user-controller';
import { captureEvent } from 'posthog';
import React, {
  ChangeEvent,
  FormEvent,
  useContext,
  useEffect,
  useState,
} from 'react';
import { goToPage, RouteName } from 'routes';
import { history } from '../../routes/routes';
import styles from './sign-in.module.scss';

type Mandatory = {
  email: string;
  password: string;
};

const mandatoryFieldsErrors: Mandatory = {
  email: '',
  password: '',
};

const SignIn: React.FC = () => {
  const [userState, setUserState] = useState<UserSignInType>(new UserSignIn());
  const [errors, setErrors] = useState<MandatoryFieldsType>(
    mandatoryFieldsErrors,
  );
  const [inputPassword, setInputPassword] = useState<boolean>(true);
  const [fetching, setFetching] = useState(true);
  const [showSignIn, setShowSignIn] = useState(false);
  const [showError, setShowError] = useState(false);

  const [showVideo, setShowVideo] = useState(false);

  const { state } = useContext(AppContext);
  const isMobileOrTablet = useMediaQuery(`(max-width: ${Breakpoints.md}px)`);

  const previousUrl = LocalStorageApi.get('previous-url');

  const auth = async () => {
    if (state.logged) {
      const wasRedirected = redictDependingFailingStatus(state.data.user);

      if (!wasRedirected) {
        goToPage(RouteName.Vaults);
      }

      return;
    }

    setFetching(true);

    try {
      const data = await UserController.me();

      const wasRedirected = redictDependingFailingStatus(data.user);

      if (!wasRedirected) {
        goToPage(RouteName.Vaults);
      }
    } catch (e) {
      setShowSignIn(true);
    } finally {
      setFetching(false);
    }
  };

  const getEmailQueryParam = () =>
    new URLSearchParams(window.location.search).get('email') || null;

  useEffect(() => {
    auth();

    const contributorEmail = getEmailQueryParam();

    if (contributorEmail) {
      setUserState((prevUserState) => ({
        ...prevUserState,
        email: contributorEmail,
      }));
    }

    return () => {
      if (!hasNotUserValidatedEmail(state.data.user)) {
        LocalStorageApi.set('previous-url', '');
      }
    };
  }, []);

  const addBackEndErrorsToFields = (backEndErrors: MandatoryFieldsType) => {
    const errorsCopy = { ...errors };

    Object.keys(errorsCopy).forEach((field) => {
      errorsCopy[field] = backEndErrors[field]
        ? backEndErrors[field].toUpperCase()
        : '';
    });

    setErrors(errorsCopy);
  };

  const validateField = <T extends keyof Mandatory>(field: T) => {
    const emptyFieldMessage =
      userState[field].trim() === ''
        ? `${field.toUpperCase()} shouldn't be empty`
        : '';
    setErrors((prevState) => ({
      ...prevState,
      [field]: emptyFieldMessage.toUpperCase(),
    }));

    if (field === 'email') {
      const emailMessage = !isEmailValid(userState[field])
        ? 'invalid email'
        : '';
      setErrors((prevState) => ({
        ...prevState,
        [field]: emailMessage.toUpperCase(),
      }));
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    Object.keys(mandatoryFieldsErrors).forEach((field) => {
      validateField(field as keyof typeof mandatoryFieldsErrors);
    });

    setFetching(true);

    try {
      const data = await UserController.signInUser(userState);

      trackCustomEvent('logIn');

      const wasRedirected = redictDependingFailingStatus(data.user);

      if (!wasRedirected) {
        // Don't redirect back to auth pages
        const isAuthPage =
          previousUrl &&
          (previousUrl.includes('signIn') ||
            previousUrl.includes('signUp') ||
            previousUrl.includes('forgotPassword'));
        if (previousUrl && !isAuthPage) {
          history.push(previousUrl);
        } else {
          goToPage(RouteName.Vaults);
        }
      }
    } catch (err: any) {
      const errorCredentials = err?.errors?.find(
        (e: any) => e.errorCode === errorsCode.credentialsError,
      );

      if (errorCredentials) {
        const objectErrors = {
          email: ' ',
          password: 'Incorrect email or password.',
        };

        addBackEndErrorsToFields!(objectErrors);
      } else {
        setShowError(true);
      }
      setFetching(false);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setUserState((prevUserState) => ({ ...prevUserState, [name]: value }));
  };

  const showSignUpButton = () => (
    <div className={styles.footer}>
      <p className="text__body__bold__small__gray700">Don’t have an account?</p>

      <Button
        buttonStyle={ButtonStyle.PrimaryGhost}
        buttonSize={ButtonSize.Small}
        onClick={() => goToPage(RouteName.SignUp)}
        dataCy={CypressIds.goToSignUp}
      >
        Sign up
      </Button>
    </div>
  );

  const onForgotPassword = () => {
    captureEvent('forgotPassword');
    trackCustomEvent('startForgotPassword');
    goToPage(RouteName.ForgotPassword);
  };

  const showSignInView = () => (
    <form
      noValidate
      onSubmit={onSubmit}
      className={globalStyles.signBoxContent}
    >
      <div className={styles.elements}>
        <div className={styles.titleContent}>
          {!isMobileOrTablet && <Logo />}
          <div
            className={classnames(
              styles.titleView,
              'text__title1__gray800',
              'mb-15',
            )}
          >
            Log in to preserve your legacy
          </div>
          {isMobileOrTablet && (
            <button
              type="button"
              onClick={() => setShowVideo(true)}
              aria-label="Play video"
              className={styles.contentPlayButtonMobile}
            >
              <div className={styles.playButtonMobile}>
                <PlayIconFilledIcon fill="white" className={styles.icon} />
              </div>
              <span className="text__body__bold__small__brand800">
                Learn about Generational Story
              </span>
            </button>
          )}
        </div>

        <div className="mb-6">
          <Input
            id="email"
            name="email"
            label="Email"
            value={userState.email}
            type="email"
            errorMessage={errors.email}
            onChange={handleChange}
            onBlur={() => validateField('email')}
            dataCy={CypressIds.emailSignin}
            innerClassName={styles.input}
          />
        </div>
        <div>
          <Input
            id="password"
            name="password"
            label="Password"
            value={userState.password}
            type={inputPassword ? 'password' : 'text'}
            errorMessage={errors.password}
            withIconEnd={
              <EyeIcon onClick={() => setInputPassword(!inputPassword)} />
            }
            onChange={handleChange}
            onBlur={() => validateField('password')}
            dataCy={CypressIds.passwordSignin}
            innerClassName={styles.input}
          />
        </div>
        <Button
          buttonStyle={ButtonStyle.PrimaryGhost}
          buttonSize={ButtonSize.Small}
          className={classnames(styles.forgotLink)}
          onClick={() => onForgotPassword()}
        >
          Forgot your password?
        </Button>

        <Button
          className={classnames(styles.loginButton, 'my-20')}
          buttonType={ButtonType.Submit}
          buttonSize={ButtonSize.Medium}
          dataCy={CypressIds.loginButton}
        >
          {fetching ? (
            <Spinner color="white" className="spinner-inside-button" />
          ) : (
            <> Log in </>
          )}
        </Button>

        {!isBeta && showSignUpButton()}
      </div>
    </form>
  );

  const showErrorNotification = () => (
    <Notification handleClose={() => setShowError(false)} />
  );

  return (
    <div>
      {showSignIn ? (
        <div className={styles.container}>
          {showError && showErrorNotification()}
          {showVideo && (
            <LegacyVideoModal onClose={() => setShowVideo(false)} />
          )}
          {!isMobileOrTablet && (
            <LegacyVideoSection showVideo={() => setShowVideo(true)} />
          )}
          <div className={styles.contentRight}>
            <div className={styles.contentFormWrapper}>{showSignInView()}</div>
            <div className={styles.contentFooter}>
              <Footer footerType={FooterType.NoSignedUser} />
            </div>
          </div>
        </div>
      ) : (
        <Spinner />
      )}
    </div>
  );
};

export { SignIn };
